import { ChoiceRow } from '@workspace/ui/components/choice-row'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'

import { Pause, Play, Send, Users } from 'lucide-react'
import { useCampaignToggle } from '../../hooks/use-campaign-toggle'

/**
 * Choose which campaigns run.
 *
 * This card used to render a spinner and a sentence, which said "wait" in the
 * one state whose whole point is that the founder now has something to do.
 *
 * Both campaigns are always listed, because they are channels rather than
 * contact lists: the warm-intro campaign is something you switch on, and
 * switching it on is what causes its founders to be researched. Hiding it until
 * contacts exist would hide it exactly when the founder needs to opt in.
 */
export function CampaignPickerVisual({
  startupId,
  matchCount,
  warmIntroCount,
  investorEnabled,
  warmIntroEnabled,
}: {
  startupId: string
  matchCount: number
  warmIntroCount: number
  investorEnabled: boolean
  warmIntroEnabled: boolean
}) {
  const campaigns = useCampaignToggle(startupId, {
    investor: investorEnabled,
    warmIntro: warmIntroEnabled,
  })

  // A real button rather than a clickable row: the same green Resume / neutral
  // Pause pair used on the outreach page, so switching a campaign on looks the
  // same wherever the founder does it. The row itself is no longer the target —
  // a whole-cell hit area gave no hint about what clicking would do, and the
  // badge beside it reported state without offering the verb.
  //
  // The server will not leave a founder with nothing running, so the last
  // channel on renders its control disabled and says why, rather than offering
  // a click that would be refused.
  const ToggleButton = ({ channel, on }: { channel: 'investor' | 'warm_intro'; on: boolean }) => {
    const locked = on && campaigns.isLastEnabled(channel)
    return (
      <SecondaryButton
        size="sm"
        tone={on ? 'neutral' : 'success'}
        icon={on ? <Pause /> : <Play />}
        disabled={locked || campaigns.isPending}
        title={locked ? 'At least one campaign has to stay on' : undefined}
        onClick={() => campaigns.toggle(channel)}
      >
        {on ? 'Disable' : 'Enable'}
      </SecondaryButton>
    )
  }

  return (
    <div className="rounded-xl border border-border/40 bg-card/40 px-5 py-4">
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-[26px] font-bold leading-none tracking-[-0.03em] text-foreground tabular-nums">
          {matchCount}
        </span>
        <span className="text-[13.5px] text-muted-foreground">investors match your thesis</span>
      </div>

      <div className="flex flex-col gap-2.5">
        <ChoiceRow
          icon={<Send className="size-4" />}
          title="Investor campaign"
          description={`Direct to your ${matchCount} matched investors, from your account. A drafted intro per partner.`}
          action={<ToggleButton channel="investor" on={investorEnabled} />}
        />

        <ChoiceRow
          icon={<Users className="size-4" />}
          title="Warm intro campaign"
          description={
            warmIntroCount > 0
              ? `${warmIntroCount} founders who already raised from your matched investors, asked for a warm intro to the investor.`
              : 'Asks founders who already raised from your matched investors to introduce you. We find them once you switch this on.'
          }
          action={<ToggleButton channel="warm_intro" on={warmIntroEnabled} />}
        />
      </div>

      <p className="mt-3.5 border-t border-border/40 pt-3 text-[12.5px] leading-relaxed text-muted-foreground">
        Run either or both. You review and approve every contact and every message before anything
        sends.
      </p>
    </div>
  )
}
